// @ts-nocheck
import { v4 as uuidv4 } from 'uuid';/* eslint-disable no-alert */
import { isEmpty, uniq, flatten } from 'lodash';
import { emailTest } from '../utils/methods';
import { Role } from '../members/add-member-row';
import { Member, maxError } from '../members/add-member-modal';
import {
  ExistingAccount,
  fetchExistingAccounts,
  batchExistingAccounts,
} from '../api/accounts-private';
import type { Error } from '../models/error';

interface Props {
  data: ImportData[];
  members: {
    roles: Role[];
    managed: boolean;
    total: number;
    max: number;
  };
}
export interface ImportSchema {
  all: ImportData[];
  errors: Member[];
  success: Member[];
  changes: string[];
}

export interface ImportData {
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  errors?: Error[];
}

const emptyCSV: Error = {
  type: 'Import Failed. Empty CSV Error',
  message: 'Please make sure your CSV has entries to be processed.',
};

const headerError: Error = {
  type: 'Import Failed. CSV Header Error',
  message: 'Please use first_name, last_name, email, and role as the header format.',
};

const maxImported: Error = {
  type: 'Import Failed. Max Imports Error',
  message: 'You have imported more than the maximum allowance of 1000 users.',
};

function validEmail(email: string) {
  return !isEmpty(email) && emailTest(email);
}

function addExistingAcct(email: string) {
  return `User ${email} already exists and has been added`;
}

function addDefaultRole(role: string, def: string) {
  return `The ${role} role doesn't exist and has been replaced with your default ${def} role`;
}

function validHeaders(data: ImportData) {
  return 'email' in data && 'first_name' in data && 'last_name' in data && 'role' in data;
}

const importSchema: ImportSchema | Error = {
  all: [], // all members being processed
  errors: [], // errors that need to be fixed
  success: [], // array of successful rows
  changes: [], // noted changes that aren't errors
};

const {
  all, errors, success, changes,
} = importSchema;

// FETCHES existing user accounts
async function getExistingAccounts(data: ImportData[], managed: boolean) {
  const memberEmails = data.map((m) => validEmail(m.email) && m.email.toLowerCase());
  let accounts = [];

  if (memberEmails.length <= 300) {
    const res = await fetchExistingAccounts(memberEmails, managed);
    accounts = res.users;
  } else {
    const batch = await batchExistingAccounts(memberEmails, managed, 300);
    const batchProcess = [];

    batch.forEach((response) => batchProcess.push(response.users));
    accounts = flatten([...batchProcess]);
  }

  return accounts;
}

export async function ImportMembers({ data, members }: Props) {
  const {
    roles, managed, total, max,
  } = members;
  const isMax = !!max && total > max;

  // FAILS import immediately for incorrect headers, an empty csv, or reaching max users
  if (!data.length) {
    return emptyCSV;
  } if (!validHeaders(data[0])) {
    return headerError;
  } if (isMax) {
    return maxError(max);
  } if (data.length > 1000) {
    return maxImported;
  }

  // SETS data for existing users
  function setExistingRows(existing: ExistingAccount[], defaultRole: Role) {
    existing.forEach((user) => {
      const importedRole = all.find((imported) => imported.email.toLowerCase() === user.email.toLowerCase()).role;
      const updatedRole = getRoleByExistingRoles(
        roles,
        importedRole,
        defaultRole,
      );

      const setRow = {
        _id: uuidv4(),
        email: user.email,
        errors: [],
        first_name: user.first_name,
        last_name: user.last_name,
        role_id: updatedRole.value,
        role: updatedRole,
        existing_user: true,
        imported: true,
        valid: true,
      };
      success.push(setRow);
      changes.push(addExistingAcct(user.email));
    });
  }

  // UPDATES remaining rows for new users
  function updateNewRows(defaultRole) {
    const users = usersToUpdate();

    if (users.length) {
      users.forEach((row) => {
        const updatedRole = getRoleByExistingRoles(
          roles,
          row.role,
          defaultRole,
        );

        const setRow = {
          _id: uuidv4(),
          first_name: row.first_name || '',
          last_name: row.last_name || '',
          email: row.email || '',
          role_id: updatedRole.value,
          role: updatedRole,
          existing_user: false,
          imported: true,
          valid: true,
          errors: [],
        };

        success.push(setRow);
      });
    }
  }

  // MARKS rows with errors and adds to errors bin
  function markErroredRows() {
    success.forEach((row) => addInputErrors(row));

    const errorsBin = success.filter(
      (successRow) => !!successRow.errors.length,
    );
    const errorsBinIds = !!errorsBin.length && errorsBin.map((err) => err._id);

    const removedErr = errorsBinIds
        && success.filter((item) => errorsBinIds.indexOf(item._id) === -1);
    errors.push(...errorsBin);

    if (removedErr) {
      (importSchema as ImportSchema).success = removedErr;
    }
  }

  if (validHeaders(data[0]) && !isMax) {
    const existing_accounts = await getExistingAccounts(data, managed);

    const defaultRole = roles.find((role) => role.default) ?? roles[0];
    all.push(...data);

    if (existing_accounts.length) {
      setExistingRows(existing_accounts, defaultRole);
    }

    updateNewRows(defaultRole);
    markErroredRows();

    if (changes.length) {
      (importSchema as ImportSchema).changes = uniq(changes);
    }

    // RETURNS import schema
    return importSchema as ImportSchema;
  }
}

// FILTERS existing accounts from imported data
function usersToUpdate() {
  if (!success.length) {
    return all;
  }

  const existing = success.map((user) => user.email);
  return all.filter((user) => existing.indexOf(user.email.toLowerCase()) === -1);
}

// RETURNS user role based on existing roles within the org
function getRoleByExistingRoles(roles: Role[], role: string, defaultRole: Role) {
  let newRole = {
    label: '',
    value: '',
    default: false,
  };

  if (role == null) {
    newRole = defaultRole;
    changes.push(addDefaultRole('required', defaultRole.label));
  } else {
    const existing_role = roles.filter((r) => r.label.toLowerCase() === role.trim().toLowerCase())[0];

    if (!existing_role) {
      newRole.label = defaultRole.label;
      newRole.value = defaultRole.value;
      newRole.default = true;
      changes.push(addDefaultRole(role, defaultRole.label));
    } else {
      newRole = existing_role;
    }
  }
  return newRole;
}

function addInputErrors(userInput) {
  if (!userInput.existing_user) {
    const { email, first_name, last_name } = userInput;

    if (isEmpty(email)) {
      userInput.errors.push('Email is required');
      userInput.valid = false;
    }
    if (isEmpty(first_name)) {
      userInput.errors.push('First Name is required');
      userInput.valid = false;
    }
    if (isEmpty(last_name)) {
      userInput.errors.push('Last Name is required');
      userInput.valid = false;
    }
    if (!isEmpty(email) && !validEmail(email)) {
      userInput.errors.push('Email has invalid format');
      userInput.valid = false;
    }
  }
}

export default ImportMembers;
